import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ChevronRight,
  X,
  FileText,
  Search,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { GET_BOOKINGS, GET_BOOKING_STATS, UPDATE_BOOKING_STATUS } from '../graphql/queries';
import { useMyStore } from '../hooks/useMyStore';

type Status =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'dispatched'
  | 'delivered'
  | 'fulfilled';

// Same transition map as the backend (`STATUS_TRANSITIONS`) so the UI
// only ever offers legal moves.
const NEXT_STATUSES: Record<Status, Status[]> = {
  pending: ['approved', 'rejected', 'cancelled'],
  approved: ['dispatched', 'cancelled', 'fulfilled'],
  dispatched: ['delivered', 'cancelled'],
  delivered: ['fulfilled'],
  fulfilled: [],
  rejected: [],
  cancelled: [],
};

interface BookingEntry {
  setId: string | null;
  variantId: string | null;
  quantity: number;
  unitPrice: number;
  taxRate: number | null;
  taxId: string | null;
  label: string | null;
  sku: string | null;
}

interface BookingLine {
  productId: string;
  productName: string | null;
  productCode: string | null;
  productImage: string | null;
  productType: string;
  parentUnitPrice: number;
  lineTotal: number;
  entries: BookingEntry[];
}

interface Booking {
  id: string;
  bookingNumber: string;
  fsrId: string;
  fsrName: string | null;
  fsrEmployeeId: string | null;
  retailerId: string | null;
  retailerName: string | null;
  retailerCity: string | null;
  retailerMobile: string | null;
  totalUnits: number;
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  status: Status;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  fulfilledAt: string | null;
  approvedBy: string | null;
  rejectionReason: string | null;
  notes: string | null;
  b2bOrderId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lines: BookingLine[];
}

const STATUS_TABS: { id: Status | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'fulfilled', label: 'Fulfilled' },
];

const STATUS_PILL_STYLE: Record<Status, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-700',
  dispatched: 'bg-blue-100 text-blue-700',
  delivered: 'bg-teal-100 text-teal-700',
  fulfilled: 'bg-indigo-100 text-indigo-700',
};

const fmtMoney = (n: number) =>
  n === Math.round(n) ? `₹${n.toLocaleString('en-IN')}` : `₹${n.toFixed(2)}`;

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const Bookings: React.FC<{ portal: string }> = () => {
  const [filterStatus, setFilterStatus] = useState<Status | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Booking | null>(null);
  const { store: myStore } = useMyStore();
  const isCustom = myStore?.storeType === 'custom';

  const { data, loading, refetch } = useQuery(GET_BOOKINGS, {
    variables: {
      status: filterStatus === 'all' ? null : filterStatus,
      search: search.trim() || null,
      page: 1,
      limit: 200,
    },
    fetchPolicy: 'network-only',
  });

  const { data: statsData, refetch: refetchStats } = useQuery(GET_BOOKING_STATS, {
    fetchPolicy: 'network-only',
  });

  const bookings: Booking[] = data?.getBookings?.bookings ?? [];
  const stats = statsData?.getBookingStats;

  const counts = useMemo(() => {
    if (!stats) return { all: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0, fulfilled: 0 };
    return {
      all: stats.all,
      pending: stats.pending,
      approved: stats.approved,
      rejected: stats.rejected,
      cancelled: stats.cancelled,
      fulfilled: stats.fulfilled,
    };
  }, [stats]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text">Bookings</h1>
          <p className="text-text2">Browse field bookings — read-only.</p>
        </div>
        <button
          onClick={() => {
            refetch();
            refetchStats();
          }}
          className="px-4 py-2 rounded-xl bg-white border border-border text-text2 font-bold flex items-center gap-2 hover:bg-surface"
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'All', value: stats.all, color: 'text-slate-600 bg-slate-50' },
            { label: 'Pending', value: stats.pending, color: 'text-amber-600 bg-amber-50' },
            { label: 'Approved', value: stats.approved, color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Rejected', value: stats.rejected, color: 'text-red-600 bg-red-50' },
            { label: 'Cancelled', value: stats.cancelled, color: 'text-gray-600 bg-gray-50' },
            { label: 'Fulfilled', value: stats.fulfilled, color: 'text-indigo-600 bg-indigo-50' },
          ].map((s) => (
            <div key={s.label} className={`rounded-2xl p-4 border border-border ${s.color}`}>
              <div className="text-[10px] font-black uppercase tracking-wider opacity-70">
                {s.label}
              </div>
              <div className="text-2xl font-black mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-border rounded-2xl p-4 flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-10 rounded-xl border border-border bg-surface px-3 flex items-center gap-2">
            <Search size={16} className="text-text3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search booking number, FSR or retailer…"
              className="flex-1 bg-transparent outline-none text-sm font-medium"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => {
            const active = filterStatus === t.id;
            const count = counts[t.id as keyof typeof counts] ?? 0;
            return (
              <button
                key={t.id}
                onClick={() => setFilterStatus(t.id)}
                className={`h-8 px-3 rounded-full text-[11px] font-black uppercase tracking-tight border transition-all ${
                  active
                    ? 'bg-text text-white border-text'
                    : 'bg-white border-border text-text2 hover:bg-surface'
                }`}
              >
                {t.label} · {count}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading && bookings.length === 0 ? (
          <div className="p-12 text-center text-text3">Loading bookings…</div>
        ) : bookings.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={32} className="mx-auto text-text3" />
            <p className="text-text2 font-bold mt-2">No bookings match this filter.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {bookings.map((b) => (
              <BookingRow key={b.id} booking={b} onOpen={() => setSelected(b)} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <BookingDrawer
            booking={selected}
            onClose={() => setSelected(null)}
            isCustom={isCustom}
            onStatusChanged={(updated) => {
              setSelected(updated);
              refetch();
              refetchStats();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const BookingRow: React.FC<{ booking: Booking; onOpen: () => void }> = ({ booking, onOpen }) => (
  <button
    onClick={onOpen}
    className="w-full text-left px-5 py-4 hover:bg-surface transition flex items-center gap-4"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-black text-text tracking-tight">{booking.bookingNumber}</span>
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${STATUS_PILL_STYLE[booking.status]}`}
        >
          {booking.status}
        </span>
      </div>
      <div className="text-xs text-text2 font-bold mt-1">
        {booking.retailerName ?? 'No retailer'}
        {booking.retailerCity ? ` · ${booking.retailerCity}` : ''}
      </div>
      <div className="text-[11px] text-text3 mt-1 font-bold">
        {booking.fsrName ?? booking.fsrId} · {booking.totalUnits} units · {booking.lines.length} item(s) ·{' '}
        {fmtDateTime(booking.submittedAt)}
      </div>
    </div>
    <div className="text-right shrink-0">
      <div className="text-base font-black text-primary">{fmtMoney(booking.total)}</div>
      {booking.tax > 0 && (
        <div className="text-[10px] text-text3 font-bold">incl. {fmtMoney(booking.tax)} GST</div>
      )}
    </div>
    <ChevronRight size={18} className="text-text3" />
  </button>
);

/**
 * Booking detail drawer. Read-only for RiEtail stores; custom stores get
 * status update controls (the booking lifecycle is owned in-house).
 */
const BookingDrawer: React.FC<{
  booking: Booking;
  onClose: () => void;
  isCustom: boolean;
  onStatusChanged: (updated: Booking) => void;
}> = ({ booking, onClose, isCustom, onStatusChanged }) => (
  <motion.div
    className="fixed inset-0 z-50 flex justify-end bg-black/40"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col"
      initial={{ x: 480 }}
      animate={{ x: 0 }}
      exit={{ x: 480 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="border-b border-border p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-text">{booking.bookingNumber}</h2>
          <p className="text-xs text-text2 font-bold">{fmtDateTime(booking.submittedAt)}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-surface">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <span
          className={`inline-flex px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${STATUS_PILL_STYLE[booking.status]}`}
        >
          {booking.status}
        </span>

        {isCustom && (
          <StatusUpdateCard booking={booking} onChanged={onStatusChanged} />
        )}

        <Section title="FSR">
          <Row label="Name" value={booking.fsrName ?? '—'} />
          <Row label="Employee ID" value={booking.fsrEmployeeId ?? '—'} />
        </Section>

        <Section title="Retailer">
          <Row label="Name" value={booking.retailerName ?? '—'} />
          <Row label="City" value={booking.retailerCity ?? '—'} />
          <Row label="Mobile" value={booking.retailerMobile ?? '—'} />
        </Section>

        <Section title={`Items (${booking.lines.length})`}>
          <div className="space-y-3">
            {booking.lines.map((l) => (
              <div key={l.productId} className="border border-border rounded-xl p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-surface2 overflow-hidden shrink-0 flex items-center justify-center">
                    {l.productImage ? (
                      <img
                        src={l.productImage}
                        alt={l.productName ?? ''}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileText size={16} className="text-text3" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-text">{l.productName ?? 'Untitled'}</div>
                    {l.productCode && (
                      <div className="text-[10px] text-text3 font-bold uppercase">SKU: {l.productCode}</div>
                    )}
                  </div>
                  <div className="text-sm font-black text-primary shrink-0">
                    {fmtMoney(l.lineTotal || l.entries.reduce((a, e) => a + e.unitPrice * e.quantity, 0))}
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  {l.entries
                    .filter((e) => e.quantity > 0)
                    .map((e, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="text-text2 font-bold">
                          {e.label || e.sku || 'Variant'} × {e.quantity}
                        </span>
                        <span className="text-text font-black">
                          {fmtMoney(e.unitPrice * e.quantity)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Totals">
          <Row label="Subtotal" value={fmtMoney(booking.subtotal)} />
          {booking.discount > 0 && (
            <Row label="Discount" value={`-${fmtMoney(booking.discount)}`} />
          )}
          {booking.tax > 0 && <Row label="Tax" value={fmtMoney(booking.tax)} />}
          <Row
            label="Shipping"
            value={booking.shipping > 0 ? fmtMoney(booking.shipping) : 'Free'}
          />
          <Row label="Total" value={fmtMoney(booking.total)} bold />
        </Section>

        {booking.notes && (
          <Section title="Notes">
            <p className="text-sm text-text2 font-medium">{booking.notes}</p>
          </Section>
        )}

        {booking.status !== 'pending' && (
          <Section title="Audit">
            {booking.approvedAt && (
              <Row
                label="Approved"
                value={`${fmtDateTime(booking.approvedAt)}${booking.approvedBy ? ` · ${booking.approvedBy}` : ''}`}
              />
            )}
            {booking.rejectedAt && (
              <Row
                label="Rejected"
                value={`${fmtDateTime(booking.rejectedAt)}${
                  booking.rejectionReason ? ` · ${booking.rejectionReason}` : ''
                }`}
              />
            )}
            {booking.cancelledAt && (
              <Row
                label="Cancelled"
                value={`${fmtDateTime(booking.cancelledAt)}${
                  booking.rejectionReason ? ` · ${booking.rejectionReason}` : ''
                }`}
              />
            )}
            {booking.b2bOrderId && <Row label="B2B order" value={booking.b2bOrderId} />}
          </Section>
        )}
      </div>
    </motion.div>
  </motion.div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="text-[10px] font-black text-text3 uppercase tracking-wider mb-2">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-text3 font-bold">{label}</span>
    <span className={bold ? 'text-text font-black text-base' : 'text-text font-bold'}>{value}</span>
  </div>
);

// ── Status update card (custom stores) ──────────────────────────
const StatusUpdateCard: React.FC<{
  booking: Booking;
  onChanged: (updated: Booking) => void;
}> = ({ booking, onChanged }) => {
  const transitions = NEXT_STATUSES[booking.status] || [];
  const [target, setTarget] = useState<Status | ''>(transitions[0] || '');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [updateStatus, { loading }] = useMutation(UPDATE_BOOKING_STATUS);

  React.useEffect(() => {
    setTarget(transitions[0] || '');
    setNote('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  if (transitions.length === 0) {
    return (
      <div className="bg-gray-50 border border-border rounded-2xl p-4">
        <div className="text-[11px] font-black uppercase tracking-widest text-text3 mb-1">
          Status
        </div>
        <p className="text-xs text-text2">
          This booking is in a terminal state. No further status changes are allowed.
        </p>
      </div>
    );
  }

  const submit = async () => {
    setError(null);
    if (!target) return;
    const { data } = await updateStatus({
      variables: { bookingId: booking.id, status: target, note: note.trim() || null },
    });
    const r = data?.updateBookingStatus;
    if (r?.error) {
      setError(r.message || 'Failed to update');
      return;
    }
    onChanged({ ...booking, status: r.booking.status as Status });
  };

  return (
    <div className="bg-indigo-50/40 border border-indigo-100 rounded-2xl p-4 space-y-3">
      <div className="text-[11px] font-black uppercase tracking-widest text-primary">
        Update status
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`px-2 py-1 rounded text-[11px] font-black uppercase tracking-wider ${STATUS_PILL_STYLE[booking.status]}`}
        >
          {booking.status}
        </span>
        <ArrowRight size={14} className="text-text3" />
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value as Status)}
          className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-bold text-text"
        >
          {transitions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <input
        className="input-field w-full"
        placeholder={
          target === 'rejected' || target === 'cancelled'
            ? 'Reason (recommended)'
            : 'Optional note for the FSR'
        }
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={loading || !target}
        className="btn-primary w-full disabled:opacity-60"
      >
        {loading ? 'Updating…' : `Move to ${target || ''}`}
      </button>
    </div>
  );
};
